import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AutoStatusToast from '../components/common/AutoStatusToast';
import ProgramColorPickModal, {
  type ProgramColorPickModalType,
} from '../components/ProgramColorPickModal';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { getTracks } from '../services/gradeTrackService';
import {
  BaseTimetableCellValue,
  BaseTimetableByTerm,
  BaseTimetableGrid,
  ClassScheduleSettings,
  GradeProgramColorLabels,
  GradeProgramColors,
  ProgramColorSettings,
  WeekdayKey,
  WeekdayMaxPeriod,
  createEmptyBaseTimetableByTerm,
  cloneBaseTimetableGrid,
  getBaseTimetableGridForTerm,
  getClassScheduleSettings,
  getGradeTrackSections,
  getProgramColorSettings,
  getProjectColorLabelForTerm,
  getProjectColorsForTerm,
  resolveGradeProgramColors,
  saveProgramColorSettings,
} from '../services/sectionLayoutService';
import { Grade, Track } from '../types/domain';
import { createDefaultGradeProgramColors } from '../constants/programColorPalettes';
import { emitAnnualPrerequisiteRefresh } from '../utils/annualPrerequisiteRefresh';
import { buildAnnualPeriodLabels, buildBaseTimetablePeriodLabels } from '../utils/annualTimetable';
import { shouldIgnoreAppNavigationShortcut } from '../utils/appNavigationShortcutGuard';
import {
  buildWildcardCharsByGrade,
  isTimetableTextMatchedByRegisteredWildcard,
} from '../utils/trackWildcard';
import {
  applyAnnualProgramsToGradeProgramColors,
  reconcileAnnualProgramsWithProjectColors,
} from '../utils/annualProjectColorSync';
import {
  getAnnualProjectPrograms,
  normalizeHexColorForProjectCompare,
  saveAnnualProjectPrograms,
  type AnnualProjectProgram,
} from '../utils/annualProjectProgramsStorage';
import { ensureProjectProgramScopedContentRowsForProjects } from '../services/programScopedContentService';
import BasicSettingIcon from '../components/common/BasicSettingIcon';

interface TimetableSettingsPageProps {
  workspaceId: string;
  grades: Grade[];
  /** '' = 네비바 통합 클래스 보기 */
  currentGradeId?: string;
  /** 연간시간표 대형 모달에 임베드될 때 */
  embeddedInModal?: boolean;
  /** embeddedInModal일 때 되돌리기·저장 툴바를 렌더할 DOM */
  toolbarPortalTarget?: HTMLElement | null;
  /** 연간 프로젝트와 기초시간표 프로젝트 색 연동에 사용 */
  academicYear?: number;
  /** 연간시간표와 동일 프로젝트 관리/생성 모달 열기 */
  onOpenProjectManage?: (gradeId: string) => void;
  onOpenProjectCreate?: (gradeId: string, term: 1 | 2) => void;
  /** 연간 프로젝트 목록(표시·툴팁). 없으면 팔레트 hex만 표시 */
  annualProjectProgramsByGradeId?: Record<string, AnnualProjectProgram[]>;
}

const WEEKDAY_COLUMNS: Array<{ key: WeekdayKey; label: string }> = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
];

const MAX_PERIODS = 10;

function parseClassName(raw: string): string {
  const [base] = raw.split('::');
  return base || raw;
}

/** grades.name → 클래스 블럭(타이틀 + 학년 칩) */
function parseGradeClassBlock(raw: string): { title: string; grades: number[] } {
  const [base, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const nums = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title: base, grades: nums };
}

function createEmptyGrid(): BaseTimetableGrid {
  return {
    mon: Array.from({ length: MAX_PERIODS }, () => ({ name: '', colorHex: '' })),
    tue: Array.from({ length: MAX_PERIODS }, () => ({ name: '', colorHex: '' })),
    wed: Array.from({ length: MAX_PERIODS }, () => ({ name: '', colorHex: '' })),
    thu: Array.from({ length: MAX_PERIODS }, () => ({ name: '', colorHex: '' })),
    fri: Array.from({ length: MAX_PERIODS }, () => ({ name: '', colorHex: '' })),
  };
}

function buildClassEntries(
  schedule: ClassScheduleSettings | null,
  grades: Grade[]
): Array<{ gradeId: string; label: string; weekdayMax: WeekdayMaxPeriod; periodLabels: string[] }> {
  if (!schedule) return [];
  const gradeMap = new Map(grades.map((g) => [g.id, g]));
  const entries: Array<{
    gradeId: string;
    label: string;
    weekdayMax: WeekdayMaxPeriod;
    periodLabels: string[];
  }> = [];
  schedule.sections.forEach((section) => {
    const rawMax = schedule.sectionWeekdayMax[section.id] ?? { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 };
    const rows = schedule.sectionRows[section.id] ?? [];
    const periodLabels = buildBaseTimetablePeriodLabels(rows);
    // 요일별 수업시수(교시 수)는 행 인덱스가 아니라 "수업(kind === 'class')" 행 기준으로 계산
    const weekdayMax: WeekdayMaxPeriod = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 };
    (Object.keys(weekdayMax) as WeekdayKey[]).forEach((dayKey) => {
      const limit = rawMax[dayKey];
      if (!limit || limit <= 0) {
        weekdayMax[dayKey] = 0;
        return;
      }
      const slice = rows.slice(0, Math.min(limit, rows.length));
      const classCount = slice.filter((r) => r.kind === 'class').length;
      weekdayMax[dayKey] = classCount;
    });
    section.gradeIds.forEach((gradeId) => {
      const grade = gradeMap.get(gradeId);
      if (!grade) return;
      if (entries.some((e) => e.gradeId === gradeId)) return;
      entries.push({
        gradeId,
        label: parseClassName(grade.name),
        weekdayMax,
        periodLabels,
      });
    });
  });
  return entries;
}

const BASE_TIMETABLE_SKELETON_PERIODS = 6;

const DEFAULT_GRADE_PROGRAM_COLORS: GradeProgramColors = createDefaultGradeProgramColors();

function BaseTimetableClassSectionHeader({
  classBlock,
}: {
  classBlock: { title: string; grades: number[] };
}) {
  return (
    <div className="cp-track-section-header cp-grade-track-header-row cp-class-members-section-header">
      <div className="cp-class-members-section-header-title-row cp-class-members-section-header-title-row--timetable">
        <div className="card-summary pill cp-track-class-pill">클래스 단독</div>
        <div className="cp-timetable-settings-header-title-with-class">
          <h3>기초 시간표 설정</h3>
          <div className="cp-grade-track-class-block cp-class-members-class-title">
            <span className="cp-grade-track-class-block-label">{classBlock.title}</span>
            {classBlock.grades.map((num) => (
              <span key={num} className="class-grade-chip-display">
                {num}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** 수업시간 미연동 시 월~금 × 6교시 미리보기(비활성·연한 표시) */
function BaseTimetableSkeletonPreview() {
  return (
    <div
      className="cp-base-timetable-skeleton-wrap"
      aria-hidden
    >
      <div className="cp-base-timetable-table-wrap">
        <div className="cp-class-schedule-table-wrap">
          <table className="cp-base-timetable-table cp-base-timetable-table--skeleton">
            <thead>
              <tr>
                <th>교시</th>
                {WEEKDAY_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: BASE_TIMETABLE_SKELETON_PERIODS }, (_, periodIdx) => {
                const period = periodIdx + 1;
                return (
                  <tr key={`sk-p${period}`}>
                    <td>{`${period}교시`}</td>
                    {WEEKDAY_COLUMNS.map((col) => (
                      <td key={col.key} className="cp-base-timetable-cell-disabled">
                        <span className="cp-base-timetable-disabled-mark">-</span>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function cellKey(day: WeekdayKey, periodIdx: number): string {
  return `${day}:${periodIdx}`;
}

function keyToCoords(key: string): { dayIdx: number; periodIdx: number } | null {
  const [day, period] = key.split(':');
  const dayIdx = WEEKDAY_COLUMNS.findIndex((d) => d.key === day);
  const periodIdx = Number(period);
  if (dayIdx < 0 || !Number.isInteger(periodIdx) || periodIdx < 0 || periodIdx >= MAX_PERIODS) return null;
  return { dayIdx, periodIdx };
}

function rectKeys(anchor: string, current: string): string[] {
  const a = keyToCoords(anchor);
  const b = keyToCoords(current);
  if (!a || !b) return [];
  const minDay = Math.min(a.dayIdx, b.dayIdx);
  const maxDay = Math.max(a.dayIdx, b.dayIdx);
  const minPeriod = Math.min(a.periodIdx, b.periodIdx);
  const maxPeriod = Math.max(a.periodIdx, b.periodIdx);
  const keys: string[] = [];
  for (let d = minDay; d <= maxDay; d += 1) {
    for (let p = minPeriod; p <= maxPeriod; p += 1) {
      keys.push(cellKey(WEEKDAY_COLUMNS[d].key, p));
    }
  }
  return keys;
}

/** 편제명(`Track.name`) 기준 그룹 — 셀에 넣는 표시명은 별칭 우선(`description` 없으면 편제명) */
type TimetableTrackCountGroup = { baseName: string; cellLabels: string[] };

function buildTimetableTrackGroupsByGrade(tracks: Track[]): Record<string, TimetableTrackCountGroup[]> {
  type Acc = { baseName: string; labelsOrdered: string[]; labelSet: Set<string>; firstIndex: number };
  const gradeMap = new Map<string, Map<string, Acc>>();

  tracks.forEach((t, index) => {
    const gradeId = (t.grade_id ?? '').trim();
    const base = (t.name ?? '').trim();
    if (!gradeId || !base) return;
    const label = (t.description ?? '').trim() || base;
    if (!gradeMap.has(gradeId)) gradeMap.set(gradeId, new Map());
    const byBase = gradeMap.get(gradeId)!;
    if (!byBase.has(base)) {
      byBase.set(base, { baseName: base, labelsOrdered: [], labelSet: new Set(), firstIndex: index });
    }
    const acc = byBase.get(base)!;
    if (index < acc.firstIndex) acc.firstIndex = index;
    if (!acc.labelSet.has(label)) {
      acc.labelSet.add(label);
      acc.labelsOrdered.push(label);
    }
  });

  const out: Record<string, TimetableTrackCountGroup[]> = {};
  Array.from(gradeMap.entries()).forEach(([gradeId, byBase]) => {
    out[gradeId] = Array.from(byBase.values())
      .sort((a, b) => a.firstIndex - b.firstIndex)
      .map((a) => ({ baseName: a.baseName, cellLabels: [...a.labelsOrdered] }));
  });
  return out;
}

function computeTrackGroupCountsForGrid(
  grid: BaseTimetableGrid,
  editingCell: { gradeId: string; day: WeekdayKey; periodIdx: number; draft: string } | null,
  gradeId: string,
  groups: ReadonlyArray<TimetableTrackCountGroup>,
  registeredNames: Set<string>
): Array<{
  baseName: string;
  total: number;
  details: Array<{ label: string; count: number }>;
  showSubBreakdown: boolean;
}> {
  const getCellName = (day: WeekdayKey, periodIdx: number): string => {
    const cell = grid[day]?.[periodIdx];
    const raw =
      editingCell &&
      editingCell.gradeId === gradeId &&
      editingCell.day === day &&
      editingCell.periodIdx === periodIdx
        ? editingCell.draft.trim()
        : (cell?.name ?? '').trim();
    return raw;
  };

  return groups.map((g) => {
    const details = g.cellLabels.map((label) => {
      let count = 0;
      if (label && registeredNames.has(label)) {
        (WEEKDAY_COLUMNS as Array<{ key: WeekdayKey }>).forEach((col) => {
          const day = col.key;
          for (let periodIdx = 0; periodIdx < MAX_PERIODS; periodIdx += 1) {
            const n = getCellName(day, periodIdx);
            if (n && n === label) count += 1;
          }
        });
      }
      return { label, count };
    });
    const total = details.reduce((s, d) => s + d.count, 0);
    const showSubBreakdown = g.cellLabels.length >= 1;
    return { baseName: g.baseName, total, details, showSubBreakdown };
  });
}

function getSingleSelectedCell(
  selectedCellsByGrade: Record<string, string[]>
): { gradeId: string; day: WeekdayKey; periodIdx: number } | null {
  const gradeId = Object.keys(selectedCellsByGrade).find(
    (id) => (selectedCellsByGrade[id] ?? []).length === 1
  );
  if (!gradeId) return null;
  const key = selectedCellsByGrade[gradeId]?.[0];
  if (!key) return null;
  const [dayStr, periodStr] = key.split(':');
  const periodIdx = Number(periodStr);
  if (!WEEKDAY_COLUMNS.some((c) => c.key === dayStr) || !Number.isInteger(periodIdx) || periodIdx < 0 || periodIdx >= MAX_PERIODS) {
    return null;
  }
  return { gradeId, day: dayStr as WeekdayKey, periodIdx };
}

/** 선택 영역의 좌상단 셀(dayIdx, periodIdx) 반환. 붙여넣기 원점용 */
function getSelectionTopLeft(
  selectedCellsByGrade: Record<string, string[]>
): { gradeId: string; minDayIdx: number; minPeriodIdx: number } | null {
  const gradeId = Object.keys(selectedCellsByGrade).find(
    (id) => (selectedCellsByGrade[id] ?? []).length > 0
  );
  if (!gradeId) return null;
  const keys = selectedCellsByGrade[gradeId] ?? [];
  if (keys.length === 0) return null;
  let minDayIdx = WEEKDAY_COLUMNS.length;
  let minPeriodIdx = MAX_PERIODS;
  for (const key of keys) {
    const c = keyToCoords(key);
    if (!c) continue;
    if (c.dayIdx < minDayIdx) minDayIdx = c.dayIdx;
    if (c.periodIdx < minPeriodIdx) minPeriodIdx = c.periodIdx;
  }
  if (minDayIdx >= WEEKDAY_COLUMNS.length || minPeriodIdx >= MAX_PERIODS) return null;
  return { gradeId, minDayIdx, minPeriodIdx };
}

const TimetableSettingsPage: React.FC<TimetableSettingsPageProps> = ({
  workspaceId,
  grades,
  currentGradeId = '',
  embeddedInModal = false,
  toolbarPortalTarget = null,
  academicYear,
  onOpenProjectManage,
  onOpenProjectCreate,
  annualProjectProgramsByGradeId,
}) => {
  const { setTimetableSettingsDirty, saveTimetableSettingsRef } = useContext(GradeTrackLeaveContext);
  const [loading, setLoading] = useState(true);
  const { toastState: saveToastState, showSaving, showSuccess, showError } = useAutoStatusToast(3000);
  const [programColorsByGrade, setProgramColorsByGrade] = useState<Record<string, GradeProgramColors>>({});
  const [programColorPickModal, setProgramColorPickModal] = useState<{
    gradeId: string;
    type: ProgramColorPickModalType;
    mode: 'create' | 'edit';
    initialHex?: string;
    initialLabel?: string;
    /** 프로젝트 색상일 때 학기(1|2) */
    term?: 1 | 2;
  } | null>(null);
  const [baseTimetableByGrade, setBaseTimetableByGrade] = useState<Record<string, BaseTimetableByTerm>>({});
  /** 클래스(gradeId)별 현재 편집 중인 학기(1 또는 2). 기본 1학기 */
  const [selectedTermByGrade, setSelectedTermByGrade] = useState<Record<string, 1 | 2>>({});
  const [classScheduleSettings, setClassScheduleSettings] = useState<ClassScheduleSettings | null>(null);
  /** 클래스(gradeId)별 해당 클래스 편제에 등록된 과목명·시간표명 집합 (유효 입력만) */
  const [registeredNamesByGrade, setRegisteredNamesByGrade] = useState<Record<string, Set<string>>>({});
  /** 클래스(gradeId)별 편제 와일드카드 한글자 집합 */
  const [wildcardCharsByGrade, setWildcardCharsByGrade] = useState<Record<string, Set<string>>>({});
  /** 클래스(gradeId)별 편제 그룹(저장된 트랙) — 카운트 블럭 미리 표시용 */
  const [timetableTrackGroupsByGrade, setTimetableTrackGroupsByGrade] = useState<
    Record<string, TimetableTrackCountGroup[]>
  >({});
  const [editingCell, setEditingCell] = useState<{
    gradeId: string;
    /** 편집 중인 학기 */
    term: 1 | 2;
    day: WeekdayKey;
    periodIdx: number;
    draft: string;
    mode: 'inline';
  } | null>(null);
  const [selectedCellsByGrade, setSelectedCellsByGrade] = useState<Record<string, string[]>>({});
  const [dragState, setDragState] = useState<{
    active: boolean;
    gradeId: string;
    anchorKey: string;
    currentKey: string;
  } | null>(null);

  const lastSavedSnapshotRef = useRef<string>('');
  const getSnapshotRef = useRef<() => string>(() => '');
  const prevLoadingRef = useRef(loading);
  /** 실행 취소·다시 실행: 스냅샷 스택 */
  const historyPastRef = useRef<string[]>([]);
  const historyFutureRef = useRef<string[]>([]);
  const historyPresentRef = useRef<string>('');
  const isApplyingHistoryRef = useRef(false);
  const historyDebounceTimerRef = useRef<number | null>(null);
  const [historyUiRev, setHistoryUiRev] = useState(0);
  const baseTimetableContainerRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const latestEditingInputRef = useRef<HTMLInputElement | null>(null);
  /** Shift+클릭 범위 선택의 시작점(일반 클릭마다 갱신) */
  const selectionRangeAnchorRef = useRef<{ gradeId: string; key: string } | null>(null);
  const baseTimetableByGradeRef = useRef(baseTimetableByGrade);
  baseTimetableByGradeRef.current = baseTimetableByGrade;
  const selectedTermByGradeRef = useRef(selectedTermByGrade);
  selectedTermByGradeRef.current = selectedTermByGrade;
  const programColorsByGradeRef = useRef(programColorsByGrade);
  programColorsByGradeRef.current = programColorsByGrade;
  /** 복사/잘라내기된 셀 데이터. 붙여넣기 시 사용. 잘라내기 시 원본 셀 키 저장 */
  const copiedCellsRef = useRef<{
    cells: { dayIdx: number; periodIdx: number; name: string; colorHex: string }[];
    isCut?: boolean;
    sourceGradeId?: string;
    sourceKeys?: string[];
    /** 잘라내기 원본 학기 */
    sourceTerm?: 1 | 2;
  } | null>(null);
  /** 복사 시 갱신해 붙여넣기 버튼 활성화 반영 */
  const [hasCopiedData, setHasCopiedData] = useState(false);
  /** 복사/잘라내기한 영역(점선 테두리 표시용) */
  const [copiedSourceRegion, setCopiedSourceRegion] = useState<{ gradeId: string; keys: string[] } | null>(null);
  /** 맞교환 영역 A, B (셀 N개 표시 및 스왑 시 사용). term은 영역 지정 시점의 학기 */
  const [swapRegionA, setSwapRegionA] = useState<{ gradeId: string; keys: string[]; term: 1 | 2 } | null>(null);
  const [swapRegionB, setSwapRegionB] = useState<{ gradeId: string; keys: string[]; term: 1 | 2 } | null>(null);

  const swapABounds = useMemo(() => {
    if (!swapRegionA?.keys?.length) return null;
    const coords = swapRegionA.keys.map(keyToCoords).filter((c): c is NonNullable<ReturnType<typeof keyToCoords>> => c != null);
    if (!coords.length) return null;
    return {
      minDayIdx: Math.min(...coords.map((c) => c.dayIdx)),
      maxDayIdx: Math.max(...coords.map((c) => c.dayIdx)),
      minPeriodIdx: Math.min(...coords.map((c) => c.periodIdx)),
      maxPeriodIdx: Math.max(...coords.map((c) => c.periodIdx)),
    };
  }, [swapRegionA]);

  const swapBBounds = useMemo(() => {
    if (!swapRegionB?.keys?.length) return null;
    const coords = swapRegionB.keys.map(keyToCoords).filter((c): c is NonNullable<ReturnType<typeof keyToCoords>> => c != null);
    if (!coords.length) return null;
    return {
      minDayIdx: Math.min(...coords.map((c) => c.dayIdx)),
      maxDayIdx: Math.max(...coords.map((c) => c.dayIdx)),
      minPeriodIdx: Math.min(...coords.map((c) => c.periodIdx)),
      maxPeriodIdx: Math.max(...coords.map((c) => c.periodIdx)),
    };
  }, [swapRegionB]);

  const classEntries = useMemo(
    () => buildClassEntries(classScheduleSettings, grades),
    [classScheduleSettings, grades]
  );
  const classEntryByGradeId = useMemo(
    () => new Map(classEntries.map((e) => [e.gradeId, e] as const)),
    [classEntries]
  );
  /** 클래스 멤버 페이지와 동일: 통합 보기는 등록된 모든 클래스 블럭을 세로로 나열 */
  const visibleTimetableBlocks = useMemo(() => {
    const targets = !currentGradeId ? grades : grades.filter((g) => g.id === currentGradeId);
    return targets.map((grade) => ({
      grade,
      entry: classEntryByGradeId.get(grade.id) ?? null,
    }));
  }, [grades, currentGradeId, classEntryByGradeId]);

  const getSnapshot = useCallback(
    () =>
      JSON.stringify({
        programColorsByGrade,
        baseTimetableByGrade,
      }),
    [programColorsByGrade, baseTimetableByGrade]
  );
  getSnapshotRef.current = getSnapshot;
  const isDirty =
    lastSavedSnapshotRef.current !== '' && getSnapshot() !== lastSavedSnapshotRef.current;

  useEffect(() => {
    if (loading) return;
    if (historyPresentRef.current === '') return;
    if (isApplyingHistoryRef.current) {
      if (historyDebounceTimerRef.current != null) {
        window.clearTimeout(historyDebounceTimerRef.current);
        historyDebounceTimerRef.current = null;
      }
      isApplyingHistoryRef.current = false;
      historyPresentRef.current = getSnapshotRef.current();
      return;
    }
    if (historyDebounceTimerRef.current != null) {
      window.clearTimeout(historyDebounceTimerRef.current);
    }
    historyDebounceTimerRef.current = window.setTimeout(() => {
      historyDebounceTimerRef.current = null;
      const snap = getSnapshotRef.current();
      if (snap === historyPresentRef.current) return;
      historyPastRef.current = [...historyPastRef.current, historyPresentRef.current].slice(-50);
      historyPresentRef.current = snap;
      historyFutureRef.current = [];
      setHistoryUiRev((v) => v + 1);
    }, 300);
    return () => {
      if (historyDebounceTimerRef.current != null) {
        window.clearTimeout(historyDebounceTimerRef.current);
        historyDebounceTimerRef.current = null;
      }
    };
  }, [programColorsByGrade, baseTimetableByGrade, loading]);

  const applySnapshotToState = useCallback((snap: string) => {
    const parsed = JSON.parse(snap) as {
      programColorsByGrade: Record<string, GradeProgramColors>;
      baseTimetableByGrade: Record<string, BaseTimetableByTerm>;
    };
    setProgramColorsByGrade(parsed.programColorsByGrade ?? {});
    setBaseTimetableByGrade(parsed.baseTimetableByGrade ?? {});
  }, []);

  const clearTransientTimetableUi = useCallback(() => {
    copiedCellsRef.current = null;
    setHasCopiedData(false);
    setCopiedSourceRegion(null);
    setSwapRegionA(null);
    setSwapRegionB(null);
    setDragState(null);
    setSelectedCellsByGrade({});
    setEditingCell(null);
    latestEditingInputRef.current = null;
  }, []);

  const handleUndo = useCallback(() => {
    if (historyPastRef.current.length === 0) return;
    if (historyDebounceTimerRef.current != null) {
      window.clearTimeout(historyDebounceTimerRef.current);
      historyDebounceTimerRef.current = null;
    }
    const current = getSnapshotRef.current();
    const prev = historyPastRef.current.pop()!;
    historyFutureRef.current.push(current);
    isApplyingHistoryRef.current = true;
    try {
      applySnapshotToState(prev);
      historyPresentRef.current = prev;
    } catch {
      historyPastRef.current.push(prev);
      historyFutureRef.current.pop();
      isApplyingHistoryRef.current = false;
      return;
    }
    clearTransientTimetableUi();
    setHistoryUiRev((v) => v + 1);
  }, [applySnapshotToState, clearTransientTimetableUi]);

  const handleRedo = useCallback(() => {
    if (historyFutureRef.current.length === 0) return;
    if (historyDebounceTimerRef.current != null) {
      window.clearTimeout(historyDebounceTimerRef.current);
      historyDebounceTimerRef.current = null;
    }
    const current = getSnapshotRef.current();
    const next = historyFutureRef.current.pop()!;
    historyPastRef.current.push(current);
    isApplyingHistoryRef.current = true;
    try {
      applySnapshotToState(next);
      historyPresentRef.current = next;
    } catch {
      historyFutureRef.current.push(next);
      historyPastRef.current.pop();
      isApplyingHistoryRef.current = false;
      return;
    }
    clearTransientTimetableUi();
    setHistoryUiRev((v) => v + 1);
  }, [applySnapshotToState, clearTransientTimetableUi]);

  useEffect(() => {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      const inTimetableHub = (e.target as HTMLElement).closest?.('.cp-timetable-hub-page');
      if (!inTimetableHub) return;
      if (e.key === 'z' || e.key === 'Z') {
        if (e.shiftKey) {
          if (historyFutureRef.current.length === 0) return;
          e.preventDefault();
          handleRedo();
        } else {
          if (historyPastRef.current.length === 0) return;
          e.preventDefault();
          handleUndo();
        }
      } else if (e.key === 'y' || e.key === 'Y') {
        if (historyFutureRef.current.length === 0) return;
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleUndo, handleRedo]);

  const timetableUndoRedoAvailability = useMemo(() => {
    void historyUiRev;
    return {
      canUndo: historyPastRef.current.length > 0,
      canRedo: historyFutureRef.current.length > 0,
    };
  }, [historyUiRev]);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    Promise.all([
      getProgramColorSettings(workspaceId),
      getClassScheduleSettings(workspaceId),
      getTracks(workspaceId),
      getGradeTrackSections(workspaceId),
      academicYear != null
        ? Promise.all(
            grades.map(async (g) => ({
              gradeId: g.id,
              programs: await getAnnualProjectPrograms(workspaceId, g.id, academicYear),
            }))
          )
        : Promise.resolve([] as { gradeId: string; programs: Awaited<ReturnType<typeof getAnnualProjectPrograms>> }[]),
    ])
      .then(([colorSettings, scheduleSettings, tracks, gradeTrackSections, annualProjects]) => {
        const entries = buildClassEntries(scheduleSettings, grades);
        const projectsByGrade: Record<string, (typeof annualProjects)[number]['programs']> = {};
        annualProjects.forEach(({ gradeId, programs }) => {
          projectsByGrade[gradeId] = programs;
        });
        const nextColors: Record<string, GradeProgramColors> = {};
        const mergeWithAnnual = (gradeId: string, colors: GradeProgramColors) => {
          const programs = projectsByGrade[gradeId];
          if (!programs || programs.length === 0) return colors;
          return applyAnnualProgramsToGradeProgramColors(colors, programs);
        };
        entries.forEach((e) => {
          nextColors[e.gradeId] = mergeWithAnnual(
            e.gradeId,
            resolveGradeProgramColors(colorSettings, e.gradeId)
          );
        });
        grades.forEach((g) => {
          if (!nextColors[g.id]) {
            nextColors[g.id] = mergeWithAnnual(g.id, resolveGradeProgramColors(colorSettings, g.id));
          }
        });
        setProgramColorsByGrade(nextColors);
        setBaseTimetableByGrade(colorSettings.baseTimetableByGrade ?? {});
        setClassScheduleSettings(scheduleSettings);
        const byGrade: Record<string, Set<string>> = {};
        const orderByGrade: Record<string, string[]> = {};
        tracks.forEach((t) => {
          const gradeId = t.grade_id ?? '';
          if (!byGrade[gradeId]) {
            byGrade[gradeId] = new Set();
            orderByGrade[gradeId] = [];
          }
          const base = (t.name || '').trim();
          const alias = (t.description || '').trim();
          if (base) {
            byGrade[gradeId].add(base);
            if (!orderByGrade[gradeId].includes(base)) orderByGrade[gradeId].push(base);
          }
          if (alias) byGrade[gradeId].add(alias);
        });
        setRegisteredNamesByGrade(byGrade);
        setWildcardCharsByGrade(buildWildcardCharsByGrade(gradeTrackSections));
        setTimetableTrackGroupsByGrade(buildTimetableTrackGroupsByGrade(tracks));
      })
      .catch(() => {
        // keep defaults
      })
      .finally(() => {
        setLoading(false);
      });
  }, [workspaceId, grades, academicYear]);

  useEffect(() => {
    setProgramColorsByGrade((prev) => {
      const next = { ...prev };
      let changed = false;
      grades.forEach((g) => {
        if (next[g.id] === undefined) {
          next[g.id] = { ...DEFAULT_GRADE_PROGRAM_COLORS };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [grades]);

  useEffect(() => {
    const justStoppedLoading = prevLoadingRef.current && !loading;
    prevLoadingRef.current = loading;
    if (justStoppedLoading) {
      const id = window.setTimeout(() => {
        const snap = getSnapshotRef.current();
        lastSavedSnapshotRef.current = snap;
        historyPastRef.current = [];
        historyFutureRef.current = [];
        historyPresentRef.current = snap;
        setHistoryUiRev((v) => v + 1);
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [loading]);

  useEffect(() => {
    const onMouseUp = () => {
      setDragState((prev) => (prev ? { ...prev, active: false } : prev));
    };
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  useEffect(() => {
    if (classEntries.length === 0) return;
    setBaseTimetableByGrade((prev) => {
      const next = { ...prev };
      let changed = false;
      classEntries.forEach((entry) => {
        if (!next[entry.gradeId]) {
          next[entry.gradeId] = createEmptyBaseTimetableByTerm();
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [classEntries]);

  const setCellValue = useCallback(
    (gradeId: string, day: WeekdayKey, periodIdx: number, value: string, term?: 1 | 2) => {
      const t = term ?? selectedTermByGradeRef.current[gradeId] ?? 1;
      const termKey = t === 1 ? 'term1' : 'term2';
      setBaseTimetableByGrade((prev) => {
        const byTerm = prev[gradeId] ?? createEmptyBaseTimetableByTerm();
        const existing = byTerm[termKey];
        const nextDay = [...existing[day]];
        nextDay[periodIdx] = { ...(nextDay[periodIdx] ?? { name: '', colorHex: '' }), name: value };
        return {
          ...prev,
          [gradeId]: { ...byTerm, [termKey]: { ...existing, [day]: nextDay } },
        };
      });
    },
    []
  );

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.cp-class-schedule-table-wrap')) return;
      if (target.closest('.cp-base-timetable-dedicated-palette')) return;
      if (target.closest('.cp-base-timetable-toolbar')) return;
      if (editingCell) {
        const draft = latestEditingInputRef.current?.value ?? editingCell.draft ?? '';
        setCellValue(editingCell.gradeId, editingCell.day, editingCell.periodIdx, draft);
        setEditingCell(null);
        latestEditingInputRef.current = null;
      }
      setSelectedCellsByGrade({});
      setCopiedSourceRegion(null);
    };
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCopiedSourceRegion(null);
        return;
      }
      if (e.key !== 'Enter') return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('input, textarea')) return;
      if (editingCell) {
        const draft = latestEditingInputRef.current?.value ?? editingCell.draft ?? '';
        setCellValue(editingCell.gradeId, editingCell.day, editingCell.periodIdx, draft);
        setEditingCell(null);
        latestEditingInputRef.current = null;
      }
      setSelectedCellsByGrade({});
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onDocKeyDown, true);
    };
  }, [editingCell, setCellValue]);

  const handleCopy = useCallback(() => {
    const gradeId = Object.keys(selectedCellsByGrade).find(
      (id) => (selectedCellsByGrade[id] ?? []).length > 0
    );
    if (!gradeId) return;
    const keys = selectedCellsByGrade[gradeId] ?? [];
    const activeTerm = selectedTermByGradeRef.current[gradeId] ?? 1;
    const grid = getBaseTimetableGridForTerm(baseTimetableByGradeRef.current[gradeId] ?? null, activeTerm);
    let minDayIdx = 5;
    let minPeriodIdx = 5;
    for (const key of keys) {
      const c = keyToCoords(key);
      if (!c) continue;
      if (c.dayIdx < minDayIdx) minDayIdx = c.dayIdx;
      if (c.periodIdx < minPeriodIdx) minPeriodIdx = c.periodIdx;
    }
    if (minDayIdx > 4 || minPeriodIdx > 4) return;
    const cells: { dayIdx: number; periodIdx: number; name: string; colorHex: string }[] = [];
    for (const key of keys) {
      const c = keyToCoords(key);
      if (!c) continue;
      const day = WEEKDAY_COLUMNS[c.dayIdx].key;
      const cell = grid[day][c.periodIdx];
      const name = editingCell && editingCell.gradeId === gradeId && editingCell.day === day && editingCell.periodIdx === c.periodIdx
        ? (latestEditingInputRef.current?.value ?? editingCell.draft ?? '')
        : (cell?.name ?? '');
      const colorHex = cell?.colorHex ?? '';
      cells.push({
        dayIdx: c.dayIdx - minDayIdx,
        periodIdx: c.periodIdx - minPeriodIdx,
        name: String(name).trim(),
        colorHex: typeof colorHex === 'string' ? colorHex : '',
      });
    }
    copiedCellsRef.current = { cells };
    setHasCopiedData(true);
    setCopiedSourceRegion({ gradeId, keys });
  }, [selectedCellsByGrade, editingCell]);

  const handleCut = useCallback(() => {
    const gradeId = Object.keys(selectedCellsByGrade).find(
      (id) => (selectedCellsByGrade[id] ?? []).length > 0
    );
    if (!gradeId) return;
    const keys = selectedCellsByGrade[gradeId] ?? [];
    const activeTerm = selectedTermByGradeRef.current[gradeId] ?? 1;
    const grid = getBaseTimetableGridForTerm(baseTimetableByGradeRef.current[gradeId] ?? null, activeTerm);
    let minDayIdx = 5;
    let minPeriodIdx = 5;
    for (const key of keys) {
      const c = keyToCoords(key);
      if (!c) continue;
      if (c.dayIdx < minDayIdx) minDayIdx = c.dayIdx;
      if (c.periodIdx < minPeriodIdx) minPeriodIdx = c.periodIdx;
    }
    if (minDayIdx > 4 || minPeriodIdx > 4) return;
    const cells: { dayIdx: number; periodIdx: number; name: string; colorHex: string }[] = [];
    for (const key of keys) {
      const c = keyToCoords(key);
      if (!c) continue;
      const day = WEEKDAY_COLUMNS[c.dayIdx].key;
      const cell = grid[day][c.periodIdx];
      const name = editingCell && editingCell.gradeId === gradeId && editingCell.day === day && editingCell.periodIdx === c.periodIdx
        ? (latestEditingInputRef.current?.value ?? editingCell.draft ?? '')
        : (cell?.name ?? '');
      const colorHex = cell?.colorHex ?? '';
      cells.push({
        dayIdx: c.dayIdx - minDayIdx,
        periodIdx: c.periodIdx - minPeriodIdx,
        name: String(name).trim(),
        colorHex: typeof colorHex === 'string' ? colorHex : '',
      });
    }
    copiedCellsRef.current = { cells, isCut: true, sourceGradeId: gradeId, sourceKeys: [...keys], sourceTerm: activeTerm };
    setHasCopiedData(true);
    setCopiedSourceRegion({ gradeId, keys });
  }, [selectedCellsByGrade, editingCell]);

  const handlePaste = useCallback(() => {
    const copied = copiedCellsRef.current;
    if (!copied || copied.cells.length === 0) return;
    setCopiedSourceRegion(null);
    const origin = getSelectionTopLeft(selectedCellsByGrade);
    if (!origin) return;
    const originDay = WEEKDAY_COLUMNS[origin.minDayIdx].key;
    const cellAtOrigin = copied.cells.find((c) => c.dayIdx === 0 && c.periodIdx === 0);

    const applyCutClear = (
      prev: Record<string, BaseTimetableByTerm>,
      result: Record<string, BaseTimetableByTerm>
    ): Record<string, BaseTimetableByTerm> => {
      if (!copied.isCut || !copied.sourceGradeId || !copied.sourceKeys?.length) return result;
      const srcTerm = copied.sourceTerm ?? 1;
      const srcTermKey = srcTerm === 1 ? 'term1' : 'term2';
      const byTerm = prev[copied.sourceGradeId] ?? createEmptyBaseTimetableByTerm();
      const existing = byTerm[srcTermKey];
      const next = {
        mon: existing.mon.map((c) => ({ ...c })),
        tue: existing.tue.map((c) => ({ ...c })),
        wed: existing.wed.map((c) => ({ ...c })),
        thu: existing.thu.map((c) => ({ ...c })),
        fri: existing.fri.map((c) => ({ ...c })),
      };
      for (const key of copied.sourceKeys) {
        const c = keyToCoords(key);
        if (!c) continue;
        const day = WEEKDAY_COLUMNS[c.dayIdx].key;
        next[day][c.periodIdx] = { name: '', colorHex: '' };
      }
      return { ...result, [copied.sourceGradeId]: { ...byTerm, [srcTermKey]: next } };
    };

    const targetTerm = selectedTermByGradeRef.current[origin.gradeId] ?? 1;
    const targetTermKey = targetTerm === 1 ? 'term1' : 'term2';

    if (copied.cells.length === 1) {
      const single = copied.cells[0];
      const keys = selectedCellsByGrade[origin.gradeId] ?? [];
      setBaseTimetableByGrade((prev) => {
        const byTerm = prev[origin.gradeId] ?? createEmptyBaseTimetableByTerm();
        const existing = byTerm[targetTermKey];
        const next = {
          mon: existing.mon.map((c) => ({ ...c })),
          tue: existing.tue.map((c) => ({ ...c })),
          wed: existing.wed.map((c) => ({ ...c })),
          thu: existing.thu.map((c) => ({ ...c })),
          fri: existing.fri.map((c) => ({ ...c })),
        };
        keys.forEach((key) => {
          const c = keyToCoords(key);
          if (!c) return;
          const day = WEEKDAY_COLUMNS[c.dayIdx].key;
          next[day][c.periodIdx] = { name: single.name, colorHex: single.colorHex };
        });
        return applyCutClear(prev, { ...prev, [origin.gradeId]: { ...byTerm, [targetTermKey]: next } });
      });
      if (editingCell && editingCell.gradeId === origin.gradeId && keys.includes(cellKey(editingCell.day, editingCell.periodIdx))) {
        setEditingCell((prev) => (prev ? { ...prev, draft: single.name } : prev));
      }
      if (copied.isCut) copiedCellsRef.current = { cells: copied.cells };
      return;
    }

    setBaseTimetableByGrade((prev) => {
      const byTerm = prev[origin.gradeId] ?? createEmptyBaseTimetableByTerm();
      const existing = byTerm[targetTermKey];
      const next = {
        mon: existing.mon.map((c) => ({ ...c })),
        tue: existing.tue.map((c) => ({ ...c })),
        wed: existing.wed.map((c) => ({ ...c })),
        thu: existing.thu.map((c) => ({ ...c })),
        fri: existing.fri.map((c) => ({ ...c })),
      };
      for (const cell of copied.cells) {
        const targetDayIdx = origin.minDayIdx + cell.dayIdx;
        const targetPeriodIdx = origin.minPeriodIdx + cell.periodIdx;
        if (targetDayIdx < 0 || targetDayIdx >= WEEKDAY_COLUMNS.length || targetPeriodIdx < 0 || targetPeriodIdx > 4) continue;
        const day = WEEKDAY_COLUMNS[targetDayIdx].key;
        next[day][targetPeriodIdx] = { name: cell.name, colorHex: cell.colorHex };
      }
      return applyCutClear(prev, { ...prev, [origin.gradeId]: { ...byTerm, [targetTermKey]: next } });
    });
    if (editingCell && editingCell.gradeId === origin.gradeId && editingCell.day === originDay && editingCell.periodIdx === origin.minPeriodIdx && cellAtOrigin) {
      setEditingCell((prev) => (prev ? { ...prev, draft: cellAtOrigin.name } : prev));
    }
    if (copied.isCut) copiedCellsRef.current = { cells: copied.cells };
  }, [selectedCellsByGrade, editingCell]);

  /** 선택된 활성 셀의 과목명만 비움(전담 색 등은 유지). 입력 필드·IME 중에는 동작하지 않음 */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      const gradeId = Object.keys(selectedCellsByGrade).find(
        (id) => (selectedCellsByGrade[id] ?? []).length > 0
      );
      if (!gradeId) return;
      const keys = selectedCellsByGrade[gradeId] ?? [];
      if (!keys.length) return;
      const entry = classEntryByGradeId.get(gradeId);
      if (!entry) return;
      const activeTerm = selectedTermByGradeRef.current[gradeId] ?? 1;
      const activeTermKey = activeTerm === 1 ? 'term1' : 'term2';
      e.preventDefault();
      setBaseTimetableByGrade((prev) => {
        const byTerm = prev[gradeId] ?? createEmptyBaseTimetableByTerm();
        const existing = byTerm[activeTermKey];
        const next = {
          mon: existing.mon.map((c) => ({ ...c })),
          tue: existing.tue.map((c) => ({ ...c })),
          wed: existing.wed.map((c) => ({ ...c })),
          thu: existing.thu.map((c) => ({ ...c })),
          fri: existing.fri.map((c) => ({ ...c })),
        };
        keys.forEach((k) => {
          const c = keyToCoords(k);
          if (!c) return;
          const day = WEEKDAY_COLUMNS[c.dayIdx].key;
          const periodActive = c.periodIdx + 1 <= entry.weekdayMax[day];
          if (!periodActive) return;
          const cell = next[day][c.periodIdx];
          next[day][c.periodIdx] = { ...(cell ?? { name: '', colorHex: '' }), name: '' };
        });
        return { ...prev, [gradeId]: { ...byTerm, [activeTermKey]: next } };
      });
      setEditingCell(null);
      latestEditingInputRef.current = null;
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [selectedCellsByGrade, classEntryByGradeId]);

  useEffect(() => {
    const onDocKeyDownCopyPaste = (e: KeyboardEvent) => {
      const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod || (e.key !== 'c' && e.key !== 'x' && e.key !== 'v')) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const inBaseTimetable = target.closest('.cp-base-timetable-sections');
      const inInput = target.closest('input, textarea');
      if (inInput && !inBaseTimetable) return;
      const hasSelection = Object.keys(selectedCellsByGrade).some(
        (id) => (selectedCellsByGrade[id] ?? []).length > 0
      );
      if ((e.key === 'c' || e.key === 'x') && !hasSelection) return;
      if (e.key === 'v' && (!copiedCellsRef.current?.cells?.length || !hasSelection)) return;
      e.preventDefault();
      if (e.key === 'c') handleCopy();
      else if (e.key === 'x') handleCut();
      else handlePaste();
    };
    document.addEventListener('keydown', onDocKeyDownCopyPaste, true);
    return () => document.removeEventListener('keydown', onDocKeyDownCopyPaste, true);
  }, [selectedCellsByGrade, handleCopy, handleCut, handlePaste]);

  const handleSetSwapRegionA = useCallback((gradeId: string) => {
    const keys = selectedCellsByGrade[gradeId] ?? [];
    if (!keys.length) return;
    const term = selectedTermByGradeRef.current[gradeId] ?? 1;
    setSwapRegionA({ gradeId, keys, term });
    setSelectedCellsByGrade({});
    setCopiedSourceRegion(null);
  }, [selectedCellsByGrade]);

  const handleSetSwapRegionB = useCallback((gradeId: string) => {
    const keys = selectedCellsByGrade[gradeId] ?? [];
    if (!keys.length) return;
    const term = selectedTermByGradeRef.current[gradeId] ?? 1;
    setSwapRegionB({ gradeId, keys, term });
    setSelectedCellsByGrade({});
    setCopiedSourceRegion(null);
  }, [selectedCellsByGrade]);

  /** 편집 중인 셀이면 input 현재 값/드래프트를 쓰고, 아니면 grid 값 사용 */
  const getEffectiveCellValue = useCallback(
    (grid: BaseTimetableGrid, gradeId: string, key: string) => {
      const [day, periodStr] = key.split(':');
      const periodIdx = Number(periodStr);
      const dayKey = day as WeekdayKey;
      const cell = grid[dayKey]?.[periodIdx];
      const isEditing =
        editingCell?.gradeId === gradeId &&
        editingCell?.day === dayKey &&
        editingCell?.periodIdx === periodIdx;
      const name = isEditing
        ? (latestEditingInputRef.current?.value ?? editingCell?.draft ?? '')
        : (cell?.name ?? '');
      return { name: String(name).trim(), colorHex: cell?.colorHex ?? '' };
    },
    [editingCell]
  );

  const handleSwapRegions = useCallback(() => {
    if (!swapRegionA || !swapRegionB) return;
    if (swapRegionA.keys.length !== swapRegionB.keys.length) return;
    setBaseTimetableByGrade((prev) => {
      const termKeyA = swapRegionA.term === 1 ? 'term1' : 'term2';
      const termKeyB = swapRegionB.term === 1 ? 'term1' : 'term2';
      const gridA = getBaseTimetableGridForTerm(prev[swapRegionA.gradeId] ?? null, swapRegionA.term);
      const gridB = getBaseTimetableGridForTerm(prev[swapRegionB.gradeId] ?? null, swapRegionB.term);
      const valuesA = swapRegionA.keys.map((key) =>
        getEffectiveCellValue(gridA, swapRegionA.gradeId, key)
      );
      const valuesB = swapRegionB.keys.map((key) =>
        getEffectiveCellValue(gridB, swapRegionB.gradeId, key)
      );

      const sameGradeAndTerm = swapRegionA.gradeId === swapRegionB.gradeId && swapRegionA.term === swapRegionB.term;
      if (sameGradeAndTerm) {
        const grid = gridA;
        const nextGrid: BaseTimetableGrid = {
          mon: grid.mon.map((c) => ({ ...(c ?? { name: '', colorHex: '' }) })),
          tue: grid.tue.map((c) => ({ ...(c ?? { name: '', colorHex: '' }) })),
          wed: grid.wed.map((c) => ({ ...(c ?? { name: '', colorHex: '' }) })),
          thu: grid.thu.map((c) => ({ ...(c ?? { name: '', colorHex: '' }) })),
          fri: grid.fri.map((c) => ({ ...(c ?? { name: '', colorHex: '' }) })),
        };
        for (let i = 0; i < swapRegionA.keys.length; i++) {
          const keyA = swapRegionA.keys[i];
          const keyB = swapRegionB.keys[i];
          const partsA = keyA.split(':');
          const partsB = keyB.split(':');
          const dayA = partsA[0] as WeekdayKey;
          const dayB = partsB[0] as WeekdayKey;
          const periodIdxA = parseInt(partsA[1], 10);
          const periodIdxB = parseInt(partsB[1], 10);
          if (!WEEKDAY_COLUMNS.some((c) => c.key === dayA) || !WEEKDAY_COLUMNS.some((c) => c.key === dayB)) continue;
          if (!Number.isInteger(periodIdxA) || !Number.isInteger(periodIdxB) || periodIdxA < 0 || periodIdxB < 0) continue;
          nextGrid[dayB][periodIdxB] = { name: valuesA[i].name, colorHex: valuesA[i].colorHex };
          nextGrid[dayA][periodIdxA] = { name: valuesB[i].name, colorHex: valuesB[i].colorHex };
        }
        const byTermA = prev[swapRegionA.gradeId] ?? createEmptyBaseTimetableByTerm();
        return { ...prev, [swapRegionA.gradeId]: { ...byTermA, [termKeyA]: nextGrid } };
      }

      const nextA = {
        mon: gridA.mon.map((c) => ({ ...c })),
        tue: gridA.tue.map((c) => ({ ...c })),
        wed: gridA.wed.map((c) => ({ ...c })),
        thu: gridA.thu.map((c) => ({ ...c })),
        fri: gridA.fri.map((c) => ({ ...c })),
      };
      const nextB = {
        mon: gridB.mon.map((c) => ({ ...c })),
        tue: gridB.tue.map((c) => ({ ...c })),
        wed: gridB.wed.map((c) => ({ ...c })),
        thu: gridB.thu.map((c) => ({ ...c })),
        fri: gridB.fri.map((c) => ({ ...c })),
      };
      swapRegionA.keys.forEach((key, i) => {
        const [day, periodStr] = key.split(':');
        const periodIdx = Number(periodStr);
        nextA[day as WeekdayKey][periodIdx] = valuesB[i];
      });
      swapRegionB.keys.forEach((key, i) => {
        const [day, periodStr] = key.split(':');
        const periodIdx = Number(periodStr);
        nextB[day as WeekdayKey][periodIdx] = valuesA[i];
      });
      const byTermA2 = prev[swapRegionA.gradeId] ?? createEmptyBaseTimetableByTerm();
      const byTermB2 = prev[swapRegionB.gradeId] ?? createEmptyBaseTimetableByTerm();
      return {
        ...prev,
        [swapRegionA.gradeId]: { ...byTermA2, [termKeyA]: nextA },
        [swapRegionB.gradeId]: { ...byTermB2, [termKeyB]: nextB },
      };
    });
    setSwapRegionA(null);
    setSwapRegionB(null);
    setEditingCell(null);
  }, [swapRegionA, swapRegionB, getEffectiveCellValue]);

  useEffect(() => {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const onDocKeyDownSwap = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'q' && e.key !== 'w' && e.key !== 'e')) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const inBaseTimetable = target.closest('.cp-base-timetable-sections');
      const inInput = target.closest('input, textarea');
      if (inInput && !inBaseTimetable) return;
      const gradeId = Object.keys(selectedCellsByGrade).find(
        (id) => (selectedCellsByGrade[id] ?? []).length > 0
      );
      const keys = gradeId ? (selectedCellsByGrade[gradeId] ?? []) : [];
      if (e.key === 'q') {
        if (keys.length) {
          e.preventDefault();
          const term = selectedTermByGradeRef.current[gradeId!] ?? 1;
          setSwapRegionA({ gradeId: gradeId!, keys, term });
          setSelectedCellsByGrade({});
          setCopiedSourceRegion(null);
        }
      } else if (e.key === 'w') {
        if (keys.length) {
          e.preventDefault();
          const term = selectedTermByGradeRef.current[gradeId!] ?? 1;
          setSwapRegionB({ gradeId: gradeId!, keys, term });
          setSelectedCellsByGrade({});
          setCopiedSourceRegion(null);
        }
      } else if (e.key === 'e') {
        if (swapRegionA && swapRegionB && swapRegionA.keys.length === swapRegionB.keys.length) {
          e.preventDefault();
          handleSwapRegions();
        }
      }
    };
    document.addEventListener('keydown', onDocKeyDownSwap, true);
    return () => document.removeEventListener('keydown', onDocKeyDownSwap, true);
  }, [selectedCellsByGrade, swapRegionA, swapRegionB, handleSwapRegions]);

  const setCellColor = useCallback(
    (gradeId: string, day: WeekdayKey, periodIdx: number, colorHex: string, term?: 1 | 2) => {
      const t = term ?? selectedTermByGradeRef.current[gradeId] ?? 1;
      const termKey = t === 1 ? 'term1' : 'term2';
      setBaseTimetableByGrade((prev) => {
        const byTerm = prev[gradeId] ?? createEmptyBaseTimetableByTerm();
        const existing = byTerm[termKey];
        const nextDay = [...existing[day]];
        nextDay[periodIdx] = { ...(nextDay[periodIdx] ?? { name: '', colorHex: '' }), colorHex };
        return {
          ...prev,
          [gradeId]: { ...byTerm, [termKey]: { ...existing, [day]: nextDay } },
        };
      });
    },
    []
  );

  const commitEdit = useCallback(
    (overrideDraft?: string) => {
      if (!editingCell) return;
      const nextValue = overrideDraft !== undefined ? String(overrideDraft) : (editingCell.draft ?? '');
      setCellValue(editingCell.gradeId, editingCell.day, editingCell.periodIdx, nextValue);
      setEditingCell(null);
    },
    [editingCell, setCellValue]
  );

  const handleCellMouseDown = useCallback(
    (gradeId: string, term: 1 | 2, day: WeekdayKey, periodIdx: number, shiftKey: boolean) => {
      const key = cellKey(day, periodIdx);
      const inputEl = latestEditingInputRef.current;
      const isSameCell =
        editingCell &&
        editingCell.gradeId === gradeId &&
        editingCell.term === term &&
        editingCell.day === day &&
        editingCell.periodIdx === periodIdx;
      if (editingCell && !isSameCell) {
        const valueToSave = inputEl?.value ?? editingCell.draft ?? '';
        commitEdit(valueToSave);
      }

      // 활성 학기 갱신 (ref도 즉시 갱신해 이후 동기적 읽기에 반영)
      if (selectedTermByGradeRef.current[gradeId] !== term) {
        selectedTermByGradeRef.current = { ...selectedTermByGradeRef.current, [gradeId]: term };
        setSelectedTermByGrade((prev) => ({ ...prev, [gradeId]: term }));
      }

      if (
        shiftKey &&
        selectionRangeAnchorRef.current &&
        selectionRangeAnchorRef.current.gradeId === gradeId &&
        selectionRangeAnchorRef.current.key
      ) {
        const anchorKey = selectionRangeAnchorRef.current.key;
        setSelectedCellsByGrade({ [gradeId]: rectKeys(anchorKey, key) });
        setDragState(null);
        setCopiedSourceRegion(null);
        return;
      }

      selectionRangeAnchorRef.current = { gradeId, key };
      setSelectedCellsByGrade({ [gradeId]: [key] });
      setDragState({ active: true, gradeId, anchorKey: key, currentKey: key });
    },
    [commitEdit, editingCell]
  );

  const handleCellMouseEnter = useCallback(
    (gradeId: string, day: WeekdayKey, periodIdx: number) => {
      if (!dragState || !dragState.active) return;
      if (dragState.gradeId !== gradeId) return;
      const current = cellKey(day, periodIdx);
      setDragState({ ...dragState, currentKey: current });
      setSelectedCellsByGrade((prev) => ({
        ...prev,
        [gradeId]: rectKeys(dragState.anchorKey, current),
      }));
    },
    [dragState]
  );

  const startEditWithKey = useCallback(
    (gradeId: string, term: 1 | 2, day: WeekdayKey, periodIdx: number, initialDraft: string) => {
      setEditingCell({ gradeId, term, day, periodIdx, draft: initialDraft, mode: 'inline' });
    },
    []
  );

  const startInlineEdit = useCallback(
    (gradeId: string, term: 1 | 2, day: WeekdayKey, periodIdx: number, initialDraft: string) => {
      setEditingCell({ gradeId, term, day, periodIdx, draft: initialDraft, mode: 'inline' });
    },
    []
  );

  useEffect(() => {
    if (!editingCell) return;
    const input = latestEditingInputRef.current;
    if (!input) return;
    input.focus({ preventScroll: true });
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, [editingCell]);

  useEffect(() => {
    const single = getSingleSelectedCell(selectedCellsByGrade);
    if (single) {
      const activeTerm = selectedTermByGrade[single.gradeId] ?? 1;
      const grid = getBaseTimetableGridForTerm(baseTimetableByGrade[single.gradeId] ?? null, activeTerm);
      const currentValue = grid[single.day][single.periodIdx]?.name ?? '';
      setEditingCell((prev) => {
        if (
          prev &&
          prev.gradeId === single.gradeId &&
          prev.term === activeTerm &&
          prev.day === single.day &&
          prev.periodIdx === single.periodIdx
        ) {
          return prev;
        }
        return {
          gradeId: single.gradeId,
          term: activeTerm,
          day: single.day,
          periodIdx: single.periodIdx,
          draft: currentValue,
          mode: 'inline',
        };
      });
    } else {
      if (editingCell) {
        const draft = latestEditingInputRef.current?.value ?? editingCell.draft ?? '';
        setCellValue(editingCell.gradeId, editingCell.day, editingCell.periodIdx, draft, editingCell.term);
      }
      setEditingCell(null);
    }
  }, [selectedCellsByGrade, baseTimetableByGrade, selectedTermByGrade, editingCell, setCellValue]);

  const applyColorToSelected = useCallback(
    (
      entry: { gradeId: string; weekdayMax: WeekdayMaxPeriod },
      colorHex: string,
      term?: 1 | 2
    ) => {
      const selected = selectedCellsByGrade[entry.gradeId] ?? [];
      if (selected.length === 0) return;
      if (term === 1 || term === 2) {
        if (selectedTermByGradeRef.current[entry.gradeId] !== term) {
          selectedTermByGradeRef.current = {
            ...selectedTermByGradeRef.current,
            [entry.gradeId]: term,
          };
          setSelectedTermByGrade((prev) => ({ ...prev, [entry.gradeId]: term }));
        }
      }
      const activeTerm = term ?? selectedTermByGradeRef.current[entry.gradeId] ?? 1;
      const activeTermKey = activeTerm === 1 ? 'term1' : 'term2';
      setBaseTimetableByGrade((prev) => {
        const byTerm = prev[entry.gradeId] ?? createEmptyBaseTimetableByTerm();
        const existing = byTerm[activeTermKey];
        const next = {
          mon: existing.mon.map((c) => ({ ...c })),
          tue: existing.tue.map((c) => ({ ...c })),
          wed: existing.wed.map((c) => ({ ...c })),
          thu: existing.thu.map((c) => ({ ...c })),
          fri: existing.fri.map((c) => ({ ...c })),
        };
        selected.forEach((key) => {
          const c = keyToCoords(key);
          if (!c) return;
          const day = WEEKDAY_COLUMNS[c.dayIdx].key;
          const active = c.periodIdx + 1 <= entry.weekdayMax[day];
          if (!active) return;
          next[day][c.periodIdx] = { ...next[day][c.periodIdx], colorHex };
        });
        return { ...prev, [entry.gradeId]: { ...byTerm, [activeTermKey]: next } };
      });
      setSelectedCellsByGrade({});
    },
    [selectedCellsByGrade]
  );

  /** 1학기 기초시간표를 2학기로 복제 */
  const handleCopyTerm1ToTerm2 = useCallback((gradeId: string) => {
    setBaseTimetableByGrade((prev) => {
      const byTerm = prev[gradeId];
      if (!byTerm) return prev;
      return { ...prev, [gradeId]: { ...byTerm, term2: cloneBaseTimetableGrid(byTerm.term1) } };
    });
  }, []);

  const normalizeProgramColorHex = useCallback((hex: string) => hex.trim().toLowerCase(), []);

  const getProgramColorLabel = useCallback(
    (gradeId: string, type: ProgramColorPickModalType, hex: string, term?: 1 | 2) => {
      const gc = programColorsByGrade[gradeId] ?? DEFAULT_GRADE_PROGRAM_COLORS;
      const key = normalizeProgramColorHex(hex);
      if (!key) return '';
      if (type === 'project' && (term === 1 || term === 2)) {
        return getProjectColorLabelForTerm(gc, term, key);
      }
      return gc.labels?.[type]?.[key] ?? '';
    },
    [programColorsByGrade, normalizeProgramColorHex]
  );

  const mergeUniqueProjectHexes = useCallback((...lists: string[][]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    lists.flat().forEach((hex) => {
      const n = normalizeProgramColorHex(hex);
      if (!n || seen.has(n)) return;
      seen.add(n);
      out.push(n);
    });
    return out;
  }, [normalizeProgramColorHex]);

  const updateGradeProgramColors = useCallback(
    (gradeId: string, updater: (current: GradeProgramColors) => GradeProgramColors) => {
      setProgramColorsByGrade((prev) => {
        const current = prev[gradeId] ?? { ...DEFAULT_GRADE_PROGRAM_COLORS };
        return { ...prev, [gradeId]: updater(current) };
      });
    },
    []
  );

  const handleSubmitProgramColor = useCallback(
    (payload: { hex: string; label: string; mode: 'create' | 'edit'; originalHex?: string }) => {
      if (!programColorPickModal) return;
      const { gradeId, type, term } = programColorPickModal;
      const nextHex = normalizeProgramColorHex(payload.hex);
      if (!nextHex) return;
      const originalHex = normalizeProgramColorHex(payload.originalHex ?? '');
      updateGradeProgramColors(gradeId, (current) => {
        const labels: GradeProgramColorLabels = {
          regular: { ...(current.labels?.regular ?? {}) },
          dedicated: { ...(current.labels?.dedicated ?? {}) },
          baseExclude: { ...(current.labels?.baseExclude ?? {}) },
          project: { ...(current.labels?.project ?? {}) },
          projectTerm1: { ...(current.labels?.projectTerm1 ?? {}) },
          projectTerm2: { ...(current.labels?.projectTerm2 ?? {}) },
        };

        if (type === 'project') {
          const projectTerm = term === 2 ? 2 : 1;
          const termKey = projectTerm === 1 ? 'term1' : 'term2';
          const labelKey = projectTerm === 1 ? 'projectTerm1' : 'projectTerm2';
          const projectByTerm = {
            term1: [...(current.projectByTerm?.term1 ?? [])],
            term2: [...(current.projectByTerm?.term2 ?? [])],
          };
          const list = projectByTerm[termKey];
          if (payload.mode === 'edit') {
            if (!originalHex) return current;
            if (!list.some((hex) => normalizeProgramColorHex(hex) === originalHex)) return current;
            if (
              nextHex !== originalHex &&
              list.some((hex) => normalizeProgramColorHex(hex) === nextHex)
            ) {
              return current;
            }
            projectByTerm[termKey] = list.map((hex) =>
              normalizeProgramColorHex(hex) === originalHex ? nextHex : hex
            );
            delete labels[labelKey][originalHex];
            delete labels.project[originalHex];
          } else {
            if (list.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return current;
            projectByTerm[termKey] = [...list, nextHex];
          }
          if (payload.label.trim()) {
            labels[labelKey][nextHex] = payload.label.trim();
            labels.project[nextHex] = payload.label.trim();
          } else {
            delete labels[labelKey][nextHex];
            delete labels.project[nextHex];
          }
          return {
            ...current,
            projectByTerm,
            project: mergeUniqueProjectHexes(projectByTerm.term1, projectByTerm.term2),
            labels,
          };
        }

        const list = [...(current[type] ?? [])];
        if (payload.mode === 'edit') {
          if (!originalHex) return current;
          if (!list.some((hex) => normalizeProgramColorHex(hex) === originalHex)) return current;
          if (
            nextHex !== originalHex &&
            list.some((hex) => normalizeProgramColorHex(hex) === nextHex)
          ) {
            return current;
          }
          const nextList = list.map((hex) =>
            normalizeProgramColorHex(hex) === originalHex ? nextHex : hex
          );
          delete labels[type][originalHex];
          if (payload.label.trim()) labels[type][nextHex] = payload.label.trim();
          else delete labels[type][nextHex];
          return { ...current, [type]: nextList, labels };
        }
        if (list.some((hex) => normalizeProgramColorHex(hex) === nextHex)) return current;
        if (payload.label.trim()) labels[type][nextHex] = payload.label.trim();
        else delete labels[type][nextHex];
        return { ...current, [type]: [...list, nextHex], labels };
      });
      setProgramColorPickModal(null);
    },
    [programColorPickModal, normalizeProgramColorHex, updateGradeProgramColors, mergeUniqueProjectHexes]
  );

  const handleDeleteProgramColor = useCallback(() => {
    if (!programColorPickModal?.initialHex) return;
    const { gradeId, type, initialHex, term } = programColorPickModal;
    const target = normalizeProgramColorHex(initialHex);
    if (!target) return;
    updateGradeProgramColors(gradeId, (current) => {
      const labels: GradeProgramColorLabels = {
        regular: { ...(current.labels?.regular ?? {}) },
        dedicated: { ...(current.labels?.dedicated ?? {}) },
        baseExclude: { ...(current.labels?.baseExclude ?? {}) },
        project: { ...(current.labels?.project ?? {}) },
        projectTerm1: { ...(current.labels?.projectTerm1 ?? {}) },
        projectTerm2: { ...(current.labels?.projectTerm2 ?? {}) },
      };

      if (type === 'project') {
        const projectTerm = term === 2 ? 2 : 1;
        const termKey = projectTerm === 1 ? 'term1' : 'term2';
        const labelKey = projectTerm === 1 ? 'projectTerm1' : 'projectTerm2';
        const projectByTerm = {
          term1: [...(current.projectByTerm?.term1 ?? [])],
          term2: [...(current.projectByTerm?.term2 ?? [])],
        };
        projectByTerm[termKey] = projectByTerm[termKey].filter(
          (hex) => normalizeProgramColorHex(hex) !== target
        );
        delete labels[labelKey][target];
        const stillUsed =
          projectByTerm.term1.some((hex) => normalizeProgramColorHex(hex) === target) ||
          projectByTerm.term2.some((hex) => normalizeProgramColorHex(hex) === target);
        if (!stillUsed) delete labels.project[target];
        return {
          ...current,
          projectByTerm,
          project: mergeUniqueProjectHexes(projectByTerm.term1, projectByTerm.term2),
          labels,
        };
      }

      delete labels[type][target];
      return {
        ...current,
        [type]: (current[type] ?? []).filter((hex) => normalizeProgramColorHex(hex) !== target),
        labels,
      };
    });
    setProgramColorPickModal(null);
  }, [programColorPickModal, normalizeProgramColorHex, updateGradeProgramColors, mergeUniqueProjectHexes]);

  const baseTimetableColorPaletteGroups = useMemo(
    () =>
      [
        { label: '고정(행사)', modalType: 'regular' as const, colorsKey: 'regular' as const },
        { label: '전담', modalType: 'dedicated' as const, colorsKey: 'dedicated' as const },
        { label: '라벨링 색상', modalType: 'baseExclude' as const, colorsKey: 'baseExclude' as const },
        { label: '프로젝트', modalType: 'project' as const, colorsKey: 'project' as const },
      ] as const,
    []
  );

  const handleSaveAll = useCallback(async () => {
    setLoading(true);
    showSaving();
    try {
      const baseTimetable = baseTimetableByGradeRef.current;
      const colorsSnapshot = programColorsByGradeRef.current;
      const colorsByGrade: Record<string, GradeProgramColors> = {};
      classEntries.forEach((e) => {
        colorsByGrade[e.gradeId] = colorsSnapshot[e.gradeId] ?? { ...DEFAULT_GRADE_PROGRAM_COLORS };
      });
      const settings: ProgramColorSettings = {
        colorsByGrade,
        baseTimetableByGrade: baseTimetable,
        workspaceFallback: null,
      };
      await saveProgramColorSettings(workspaceId, settings);
      if (academicYear != null) {
        await Promise.all(
          classEntries.map(async (e) => {
            const colors = colorsByGrade[e.gradeId];
            if (!colors) return;
            const prev = await getAnnualProjectPrograms(workspaceId, e.gradeId, academicYear);
            const next = reconcileAnnualProgramsWithProjectColors(prev, colors);
            await saveAnnualProjectPrograms(workspaceId, e.gradeId, academicYear, next);
            try {
              await ensureProjectProgramScopedContentRowsForProjects(
                workspaceId,
                e.gradeId,
                academicYear,
                next
              );
            } catch {
              /* 색상 저장은 유지 */
            }
          })
        );
      }
      emitAnnualPrerequisiteRefresh(workspaceId, ['timetable_settings']);
      const saved = getSnapshotRef.current();
      lastSavedSnapshotRef.current = saved;
      historyPastRef.current = [];
      historyFutureRef.current = [];
      historyPresentRef.current = saved;
      setHistoryUiRev((v) => v + 1);
      showSuccess();
    } catch (error: any) {
      showError(error?.message ?? '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [academicYear, classEntries, workspaceId, showError, showSaving, showSuccess]);

  const handleDiscardUnsaved = useCallback(() => {
    const raw = lastSavedSnapshotRef.current;
    if (!raw) return;
    if (historyDebounceTimerRef.current != null) {
      window.clearTimeout(historyDebounceTimerRef.current);
      historyDebounceTimerRef.current = null;
    }
    try {
      applySnapshotToState(raw);
    } catch {
      return;
    }
    clearTransientTimetableUi();
    historyPastRef.current = [];
    historyFutureRef.current = [];
    historyPresentRef.current = raw;
    setHistoryUiRev((v) => v + 1);
  }, [applySnapshotToState, clearTransientTimetableUi]);

  useEffect(() => {
    setTimetableSettingsDirty(isDirty);
    return () => setTimetableSettingsDirty(false);
  }, [isDirty, setTimetableSettingsDirty]);

  useEffect(() => {
    saveTimetableSettingsRef.current = { save: handleSaveAll, discard: handleDiscardUnsaved };
    return () => {
      saveTimetableSettingsRef.current = null;
    };
  }, [handleSaveAll, handleDiscardUnsaved, saveTimetableSettingsRef]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      const inTimetableHub = (e.target as HTMLElement).closest?.('.cp-timetable-hub-page');
      if (!inTimetableHub) return;
      if (e.key !== 's' && e.key !== 'S') return;
      if (e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();
      if (!loading && isDirty) {
        void handleSaveAll();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleSaveAll, isDirty, loading]);

  const titleActions = (
    <div className="cp-timetable-settings-title-actions">
      <ProgramToolbarUndoRedoButtons
        canUndo={timetableUndoRedoAvailability.canUndo}
        canRedo={timetableUndoRedoAvailability.canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
        disabled={loading}
      />
      <button
        type="button"
        className="cp-btn-save-all"
        onClick={() => {
          if (loading || !isDirty) return;
          void handleSaveAll();
        }}
        disabled={loading}
        aria-disabled={loading || !isDirty ? true : undefined}
      >
        {isDirty ? '저장하기' : '변경사항 없음'}
      </button>
    </div>
  );

  const settingsBody = (
    <>
      {grades.length === 0 ? (
        <article className="cp-card cp-base-timetable-preview-card">
          <div className="cp-track-section-header cp-grade-track-header-row cp-class-members-section-header">
            <div className="cp-class-members-section-header-title-row cp-class-members-section-header-title-row--timetable">
              <div className="card-summary pill cp-track-class-pill">클래스 단독</div>
              <h3>기초 시간표 설정</h3>
            </div>
            <span className="cp-section-class-empty-hint cp-class-members-empty-hint-stacked">
              <span className="cp-class-members-empty-hint-mark" aria-hidden="true">
                *
              </span>
              <span className="cp-class-members-empty-hint-lines">
                <span className="cp-class-members-empty-hint-line">클래스를 등록해주세요.</span>
                <span className="cp-class-members-empty-hint-line">클래스를 등록하면 섹션이 활성화 됩니다.</span>
              </span>
            </span>
          </div>
          <BaseTimetableSkeletonPreview />
        </article>
      ) : (
        <div
          ref={baseTimetableContainerRef}
          className="cp-base-timetable-sections cp-base-timetable-page-classes"
          role="region"
          aria-label="기초 시간표 설정"
        >
          {visibleTimetableBlocks.map(({ grade, entry }) => {
              const classBlock = parseGradeClassBlock(grade.name);
              const gradeId = entry?.gradeId ?? grade.id;
              const gc = programColorsByGrade[gradeId] ?? { ...DEFAULT_GRADE_PROGRAM_COLORS };
              if (!entry) {
                return (
                  <article
                    key={grade.id}
                    className="cp-card cp-base-timetable-preview-card cp-base-timetable-class-card"
                  >
                    <BaseTimetableClassSectionHeader
                      classBlock={classBlock}
                    />
                    <article className="cp-card academic-calendar-settings-card cp-timetable-settings-nested-card cp-timetable-settings-base-input-card">
                      <div className="cp-grade-track-header-row">
                        <h3>기초 시간표 입력</h3>
                      </div>
                      <div className="cp-timetable-settings-base-grid-inner">
                        <BaseTimetableSkeletonPreview />
                      </div>
                    </article>
                  </article>
                );
              }
              const byTerm = baseTimetableByGrade[entry.gradeId];
              const term1Grid = getBaseTimetableGridForTerm(byTerm ?? null, 1);
              const term2Grid = getBaseTimetableGridForTerm(byTerm ?? null, 2);

              /** 단일 학기 그리드 테이블 렌더 헬퍼 */
              const renderTermGrid = (termGrid: BaseTimetableGrid, term: 1 | 2) => {
                const isActiveTerm = (selectedTermByGrade[entry.gradeId] ?? 1) === term;
                const projectHexSet = new Set<string>();
                getProjectColorsForTerm(gc, term).forEach((hex) => {
                  const n = normalizeHexColorForProjectCompare(hex);
                  if (n) projectHexSet.add(n);
                });
                (annualProjectProgramsByGradeId?.[entry.gradeId] ?? []).forEach((p) => {
                  if (p.termNumber !== term) return;
                  const n = normalizeHexColorForProjectCompare(p.colorHex);
                  if (n) projectHexSet.add(n);
                });
                return (
                  <>
                  <div className="cp-base-timetable-table-wrap">
                    <div className="cp-class-schedule-table-wrap">
                      <table
                        className={`cp-base-timetable-table cp-base-timetable-table--term${term}`}
                      >
                        <thead>
                          <tr>
                            <th>교시</th>
                            {WEEKDAY_COLUMNS.map((col) => (
                              <th key={`${entry.gradeId}-t${term}-${col.key}`}>{col.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                        {(() => {
                          const maxPeriodByDay = WEEKDAY_COLUMNS.map((col) => entry.weekdayMax[col.key]);
                          const maxPeriod = Math.max(1, Math.min(MAX_PERIODS, Math.max(...maxPeriodByDay, 1)));
                          return Array.from({ length: maxPeriod }, (_, periodIdx) => {
                          const period = periodIdx + 1;
                          return (
                            <tr key={`${entry.gradeId}-t${term}-p${period}`}>
                              <td>
                                {(entry.periodLabels[periodIdx] ?? '').trim() || `${period}교시`}
                              </td>
                              {WEEKDAY_COLUMNS.map((col) => {
                                const active = period <= entry.weekdayMax[col.key];
                                const value =
                                  termGrid[col.key][periodIdx] ?? ({ name: '', colorHex: '' } as BaseTimetableCellValue);
                                const editing =
                                  editingCell?.gradeId === entry.gradeId &&
                                  editingCell?.term === term &&
                                  editingCell.day === col.key &&
                                  editingCell.periodIdx === periodIdx;
                                const inlineEditing = editing && editingCell?.mode === 'inline';
                                const selectedKeys = isActiveTerm ? (selectedCellsByGrade[entry.gradeId] ?? []) : [];
                                const selectedSet = new Set(selectedKeys);
                                const key = cellKey(col.key, periodIdx);
                                const selected = selectedSet.has(key);
                                const multiSelected = selectedKeys.length > 1;
                                const singleSelected = selectedKeys.length === 1 && selected;
                                const edgeTop = selected && multiSelected && !selectedSet.has(cellKey(col.key, periodIdx - 1));
                                const edgeBottom = selected && multiSelected && !selectedSet.has(cellKey(col.key, periodIdx + 1));
                                const dayIdx = WEEKDAY_COLUMNS.findIndex((d) => d.key === col.key);
                                const leftDay = dayIdx > 0 ? WEEKDAY_COLUMNS[dayIdx - 1].key : null;
                                const rightDay =
                                  dayIdx >= 0 && dayIdx < WEEKDAY_COLUMNS.length - 1
                                    ? WEEKDAY_COLUMNS[dayIdx + 1].key
                                    : null;
                                const edgeLeft = selected && multiSelected && (!leftDay || !selectedSet.has(cellKey(leftDay, periodIdx)));
                                const edgeRight = selected && multiSelected && (!rightDay || !selectedSet.has(cellKey(rightDay, periodIdx)));
                                const copiedKeys = isActiveTerm && copiedSourceRegion?.gradeId === entry.gradeId ? (copiedSourceRegion.keys ?? []) : [];
                                const copiedSet = new Set(copiedKeys);
                                const copiedSource = copiedSet.has(key);
                                const copiedSourceSingle = copiedSource && copiedKeys.length === 1;
                                const copiedSourceMulti = copiedSource && copiedKeys.length > 1;
                                const copiedEdgeTop = copiedSourceMulti && !copiedSet.has(cellKey(col.key, periodIdx - 1));
                                const copiedEdgeBottom = copiedSourceMulti && !copiedSet.has(cellKey(col.key, periodIdx + 1));
                                const copiedEdgeLeft = copiedSourceMulti && (!leftDay || !copiedSet.has(cellKey(leftDay, periodIdx)));
                                const copiedEdgeRight = copiedSourceMulti && (!rightDay || !copiedSet.has(cellKey(rightDay, periodIdx)));
                                const inSwapA = isActiveTerm && swapRegionA?.gradeId === entry.gradeId && swapRegionA?.term === term && (swapRegionA?.keys ?? []).includes(key);
                                const inSwapB = isActiveTerm && swapRegionB?.gradeId === entry.gradeId && swapRegionB?.term === term && (swapRegionB?.keys ?? []).includes(key);
                                const swapAEdgeTop = inSwapA && swapABounds && periodIdx === swapABounds.minPeriodIdx;
                                const swapAEdgeBottom = inSwapA && swapABounds && periodIdx === swapABounds.maxPeriodIdx;
                                const swapAEdgeLeft = inSwapA && swapABounds && dayIdx === swapABounds.minDayIdx;
                                const swapAEdgeRight = inSwapA && swapABounds && dayIdx === swapABounds.maxDayIdx;
                                const swapBEdgeTop = inSwapB && swapBBounds && periodIdx === swapBBounds.minPeriodIdx;
                                const swapBEdgeBottom = inSwapB && swapBBounds && periodIdx === swapBBounds.maxPeriodIdx;
                                const swapBEdgeLeft = inSwapB && swapBBounds && dayIdx === swapBBounds.minDayIdx;
                                const swapBEdgeRight = inSwapB && swapBBounds && dayIdx === swapBBounds.maxDayIdx;
                                const registeredForClass = registeredNamesByGrade[entry.gradeId] ?? new Set<string>();
                                const wildcardsForClass = wildcardCharsByGrade[entry.gradeId] ?? new Set<string>();
                                const trimmedName = value.name.trim();
                                const validName =
                                  trimmedName !== '' &&
                                  (registeredForClass.has(trimmedName) ||
                                    isTimetableTextMatchedByRegisteredWildcard(trimmedName, wildcardsForClass));
                                return (
                                  <td
                                    key={`${entry.gradeId}-t${term}-${col.key}-${period}`}
                                    className={[
                                      !active ? 'cp-base-timetable-cell-disabled' : '',
                                      active && value.colorHex ? 'cp-base-timetable-cell-has-bg' : '',
                                      selected ? 'cp-base-timetable-cell-selected' : '',
                                      singleSelected ? 'cp-base-timetable-cell-selected-single' : '',
                                      selected && multiSelected ? 'cp-base-timetable-cell-selected-multi' : '',
                                      edgeTop ? 'cp-base-timetable-cell-edge-top' : '',
                                      edgeBottom ? 'cp-base-timetable-cell-edge-bottom' : '',
                                      edgeLeft ? 'cp-base-timetable-cell-edge-left' : '',
                                      edgeRight ? 'cp-base-timetable-cell-edge-right' : '',
                                      copiedSource ? 'cp-base-timetable-cell-copied-source' : '',
                                      copiedSourceSingle ? 'cp-base-timetable-cell-copied-source-single' : '',
                                      copiedSourceMulti ? 'cp-base-timetable-cell-copied-source-multi' : '',
                                      copiedEdgeTop ? 'cp-base-timetable-cell-copied-edge-top' : '',
                                      copiedEdgeBottom ? 'cp-base-timetable-cell-copied-edge-bottom' : '',
                                      copiedEdgeLeft ? 'cp-base-timetable-cell-copied-edge-left' : '',
                                      copiedEdgeRight ? 'cp-base-timetable-cell-copied-edge-right' : '',
                                      inSwapA ? 'cp-base-timetable-cell-swap-a' : '',
                                      swapAEdgeTop ? 'cp-base-timetable-cell-swap-a-edge-top' : '',
                                      swapAEdgeBottom ? 'cp-base-timetable-cell-swap-a-edge-bottom' : '',
                                      swapAEdgeLeft ? 'cp-base-timetable-cell-swap-a-edge-left' : '',
                                      swapAEdgeRight ? 'cp-base-timetable-cell-swap-a-edge-right' : '',
                                      inSwapB ? 'cp-base-timetable-cell-swap-b' : '',
                                      swapBEdgeTop ? 'cp-base-timetable-cell-swap-b-edge-top' : '',
                                      swapBEdgeBottom ? 'cp-base-timetable-cell-swap-b-edge-bottom' : '',
                                      swapBEdgeLeft ? 'cp-base-timetable-cell-swap-b-edge-left' : '',
                                      swapBEdgeRight ? 'cp-base-timetable-cell-swap-b-edge-right' : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    onMouseDown={(e) => {
                                      if (!active) return;
                                      e.preventDefault();
                                      handleCellMouseDown(entry.gradeId, term, col.key, periodIdx, e.shiftKey);
                                    }}
                                    onMouseEnter={() => {
                                      if (!active) return;
                                      handleCellMouseEnter(entry.gradeId, col.key, periodIdx);
                                    }}
                                  >
                                    {active && value.colorHex ? (
                                      <div
                                        className={
                                          'cp-base-timetable-cell-bg' +
                                          (projectHexSet.has(
                                            normalizeHexColorForProjectCompare(value.colorHex) ?? ''
                                          )
                                            ? ' cp-annual-project-grid-fill'
                                            : '')
                                        }
                                        style={{ backgroundColor: value.colorHex }}
                                        aria-hidden
                                      />
                                    ) : null}
                                    {!active ? (
                                      <span className="cp-base-timetable-disabled-mark">-</span>
                                    ) : inlineEditing ? (
                                      <input
                                        type="text"
                                        className="cp-base-timetable-cell-input"
                                        ref={(node) => {
                                          latestEditingInputRef.current = node;
                                        }}
                                        value={editingCell.draft}
                                        onChange={(e) => {
                                          const v = e.target?.value;
                                          if (v === undefined) return;
                                          setEditingCell((prev) =>
                                            prev ? { ...prev, draft: v } : prev
                                          );
                                        }}
                                        onBlur={(e) => {
                                          const v = e.currentTarget?.value;
                                          commitEdit(v !== undefined ? v : (editingCell?.draft ?? ''));
                                          latestEditingInputRef.current = null;
                                        }}
                                        onCompositionStart={() => {
                                          isComposingRef.current = true;
                                        }}
                                        onCompositionEnd={(e) => {
                                          isComposingRef.current = false;
                                          const v = e.currentTarget?.value;
                                          if (v === undefined) return;
                                          setEditingCell((prev) =>
                                            prev ? { ...prev, draft: v } : prev
                                          );
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            if (isComposingRef.current) return;
                                            e.preventDefault();
                                            const v = e.currentTarget?.value;
                                            const ec = editingCell;
                                            if (!ec) return;
                                            commitEdit(v !== undefined ? v : (ec.draft ?? ''));
                                            latestEditingInputRef.current = null;
                                            const nextPeriodIdx = ec.periodIdx + 1 >= MAX_PERIODS ? 0 : ec.periodIdx + 1;
                                            const ecGrid = getBaseTimetableGridForTerm(baseTimetableByGrade[ec.gradeId] ?? null, ec.term);
                                            const nextValue = ecGrid[ec.day][nextPeriodIdx]?.name ?? '';
                                            setSelectedCellsByGrade({ [ec.gradeId]: [cellKey(ec.day, nextPeriodIdx)] });
                                            setEditingCell({
                                              gradeId: ec.gradeId,
                                              term: ec.term,
                                              day: ec.day,
                                              periodIdx: nextPeriodIdx,
                                              draft: nextValue,
                                              mode: 'inline',
                                            });
                                          }
                                          if (e.key === 'Tab') {
                                            if (isComposingRef.current) return;
                                            e.preventDefault();
                                            const v = e.currentTarget?.value;
                                            const ec = editingCell;
                                            if (!ec) return;
                                            commitEdit(v !== undefined ? v : (ec.draft ?? ''));
                                            latestEditingInputRef.current = null;
                                            const dayIdx = WEEKDAY_COLUMNS.findIndex((d) => d.key === ec.day);
                                            const nextDayIdx = dayIdx < 0 || dayIdx >= WEEKDAY_COLUMNS.length - 1 ? 0 : dayIdx + 1;
                                            const nextDay = WEEKDAY_COLUMNS[nextDayIdx].key;
                                            const ecGrid2 = getBaseTimetableGridForTerm(baseTimetableByGrade[ec.gradeId] ?? null, ec.term);
                                            const nextValue = ecGrid2[nextDay][ec.periodIdx]?.name ?? '';
                                            setSelectedCellsByGrade({ [ec.gradeId]: [cellKey(nextDay, ec.periodIdx)] });
                                            setEditingCell({
                                              gradeId: ec.gradeId,
                                              term: ec.term,
                                              day: nextDay,
                                              periodIdx: ec.periodIdx,
                                              draft: nextValue,
                                              mode: 'inline',
                                            });
                                          }
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setEditingCell(null);
                                            latestEditingInputRef.current = null;
                                          }
                                        }}
                                        autoFocus
                                      />
                                    ) : (
                                      <div
                                        className="cp-base-timetable-cell-inner"
                                        onDoubleClick={() => startInlineEdit(entry.gradeId, term, col.key, periodIdx, value.name)}
                                        aria-label={`${entry.label} ${col.label} ${period}교시`}
                                      >
                                        {value.name.trim() === '' ? (
                                          <span className="cp-base-timetable-empty"> </span>
                                        ) : validName ? (
                                          value.name
                                        ) : (
                                          <span className="cp-base-timetable-invalid">{value.name}</span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                          });
                        })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="cp-base-timetable-name-counts cp-base-timetable-track-count-blocks">
                    {(timetableTrackGroupsByGrade[entry.gradeId] ?? []).length === 0 ? (
                      <span className="cp-base-timetable-track-count-empty">등록된 편제가 없습니다.</span>
                    ) : (
                      computeTrackGroupCountsForGrid(
                        termGrid,
                        editingCell?.term === term ? editingCell : null,
                        entry.gradeId,
                        timetableTrackGroupsByGrade[entry.gradeId] ?? [],
                        registeredNamesByGrade[entry.gradeId] ?? new Set()
                      ).map((g) => (
                        <div
                          key={`${entry.gradeId}-t${term}-tg-${g.baseName}`}
                          className="cp-base-timetable-track-count-block"
                        >
                          <div className="cp-base-timetable-track-count-block-head">
                            <span className="cp-base-timetable-track-count-base">{g.baseName}</span>
                            <span className="cp-base-timetable-track-count-total">{g.total}개</span>
                          </div>
                          {g.showSubBreakdown ? (
                            <ul className="cp-base-timetable-track-count-sublist">
                              {g.details.map((d) => (
                                <li
                                  key={`${entry.gradeId}-t${term}-${g.baseName}-${d.label}`}
                                  className={
                                    d.count === 0
                                      ? 'cp-base-timetable-track-count-subrow cp-base-timetable-track-count-subrow--zero'
                                      : 'cp-base-timetable-track-count-subrow'
                                  }
                                >
                                  <span className="cp-base-timetable-track-count-sub-label">{d.label}</span>
                                  <span className="cp-base-timetable-track-count-sub-num">{d.count}개</span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                  </>
                );
              };

              /** 학기별 색상 팔레트 (고정/전담/라벨링 공유, 프로젝트만 학기별·연간과 동일 UI) */
              const renderColorPalettes = (term: 1 | 2) => (
                <div
                  className="cp-weekly-schedule-color-palettes"
                  aria-label={`${entry.label} ${term}학기 기초 시간표 색상`}
                >
                  {baseTimetableColorPaletteGroups.map((group) => {
                    if (group.modalType === 'project') {
                      const termProjects = (annualProjectProgramsByGradeId?.[entry.gradeId] ?? []).filter(
                        (p) => p.termNumber === term
                      );
                      const fallbackColors = getProjectColorsForTerm(gc, term);
                      const swatches =
                        termProjects.length > 0
                          ? termProjects.map((p) => ({
                              key: p.id,
                              hex: p.colorHex,
                              label: p.name,
                            }))
                          : fallbackColors.map((hex) => ({
                              key: hex,
                              hex,
                              label: getProgramColorLabel(entry.gradeId, 'project', hex, term),
                            }));
                      return (
                        <div
                          key={`${entry.gradeId}-t${term}-project`}
                          className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group cp-annual-project-palette"
                          data-tutorial-color-type="project"
                        >
                          <span className="cp-base-timetable-dedicated-label">프로젝트</span>
                          <button
                            type="button"
                            className="cp-dedicated-color-btn cp-annual-project-manage-gear-btn"
                            onClick={() => {
                              if (onOpenProjectManage) onOpenProjectManage(entry.gradeId);
                              else {
                                setProgramColorPickModal({
                                  gradeId: entry.gradeId,
                                  type: 'project',
                                  mode: 'create',
                                  term,
                                });
                              }
                            }}
                            title="프로젝트 관리"
                            aria-label="프로젝트 관리"
                          >
                            <BasicSettingIcon />
                          </button>
                          <button
                            type="button"
                            className="cp-dedicated-color-btn cp-dedicated-color-none-btn"
                            onClick={() => applyColorToSelected(entry, '', term)}
                            title="프로젝트 색상없음"
                            aria-label="프로젝트 색상없음"
                          />
                          {swatches.map((swatch) => (
                            <span key={swatch.key} className="cp-annual-project-toolbar-swatch-wrap">
                              <button
                                type="button"
                                className="cp-dedicated-color-btn"
                                onClick={() => applyColorToSelected(entry, swatch.hex, term)}
                                title={swatch.label ? undefined : swatch.hex}
                                aria-label={
                                  swatch.label
                                    ? `프로젝트 ${swatch.label} (${swatch.hex})`
                                    : `프로젝트 ${swatch.hex}`
                                }
                              >
                                <span
                                  className="cp-dedicated-color-btn-inner cp-annual-project-grid-fill"
                                  style={{ backgroundColor: swatch.hex }}
                                  aria-hidden
                                />
                              </button>
                              {swatch.label ? (
                                <span className="cp-annual-project-toolbar-tooltip" role="tooltip">
                                  {swatch.label}
                                </span>
                              ) : null}
                            </span>
                          ))}
                          <button
                            type="button"
                            className="cp-dedicated-color-btn cp-dedicated-color-add-btn"
                            onClick={() => {
                              if (onOpenProjectCreate) onOpenProjectCreate(entry.gradeId, term);
                              else {
                                setProgramColorPickModal({
                                  gradeId: entry.gradeId,
                                  type: 'project',
                                  mode: 'create',
                                  term,
                                });
                              }
                            }}
                            title="프로젝트 프로그램 생성"
                            aria-label="프로젝트 프로그램 생성"
                          >
                            +
                          </button>
                        </div>
                      );
                    }

                    const colors = gc[group.colorsKey] ?? [];
                    return (
                      <div
                        key={`${entry.gradeId}-t${term}-${group.modalType}`}
                        className="cp-base-timetable-dedicated-palette cp-weekly-schedule-color-group"
                        data-tutorial-color-type={group.modalType}
                      >
                        <span className="cp-base-timetable-dedicated-label">{group.label}</span>
                        <button
                          type="button"
                          className="cp-dedicated-color-btn cp-dedicated-color-none-btn"
                          onClick={() => applyColorToSelected(entry, '', term)}
                          title={`${group.label} 색상없음`}
                          aria-label={`${group.label} 색상없음`}
                        />
                        {colors.map((hex) => {
                          const colorLabel = getProgramColorLabel(entry.gradeId, group.modalType, hex);
                          return (
                            <span
                              key={`${entry.gradeId}-t${term}-${group.modalType}-${hex}`}
                              className="cp-annual-toolbar-color-wrap"
                            >
                              <span className="cp-annual-project-toolbar-swatch-wrap">
                                <button
                                  type="button"
                                  className="cp-dedicated-color-btn"
                                  onClick={() => applyColorToSelected(entry, hex, term)}
                                  title={colorLabel ? undefined : hex}
                                  aria-label={
                                    colorLabel
                                      ? `${group.label} ${colorLabel}`
                                      : `${group.label} ${hex}`
                                  }
                                >
                                  <span
                                    className="cp-dedicated-color-btn-inner"
                                    style={{ backgroundColor: hex }}
                                    aria-hidden
                                  />
                                </button>
                                {colorLabel ? (
                                  <span className="cp-annual-project-toolbar-tooltip" role="tooltip">
                                    {colorLabel}
                                  </span>
                                ) : null}
                              </span>
                              <button
                                type="button"
                                className="cp-annual-toolbar-color-action cp-annual-toolbar-color-action-edit"
                                onClick={() =>
                                  setProgramColorPickModal({
                                    gradeId: entry.gradeId,
                                    type: group.modalType,
                                    mode: 'edit',
                                    initialHex: hex,
                                    initialLabel: colorLabel,
                                  })
                                }
                                aria-label={`${group.label} 색상 수정`}
                                title="색상/라벨 수정"
                              >
                                <img src="/edit.svg" alt="" aria-hidden />
                              </button>
                            </span>
                          );
                        })}
                        <button
                          type="button"
                          className="cp-dedicated-color-btn cp-dedicated-color-add-btn"
                          onClick={() =>
                            setProgramColorPickModal({
                              gradeId: entry.gradeId,
                              type: group.modalType,
                              mode: 'create',
                            })
                          }
                          title={`${group.label} 색상 추가`}
                          aria-label={`${group.label} 색상 추가`}
                        >
                          +
                        </button>
                      </div>
                    );
                  })}
                </div>
              );

              const renderTermToolbar = () => (
                <div className="cp-base-timetable-toolbar cp-annual-timetable-actions-inline">
                  <div className="cp-program-progress-toolbar-group" role="group" aria-label="교환 영역">
                    <button
                      type="button"
                      className="cp-btn-copy-paste cp-btn-swap-region"
                      onClick={() => handleSetSwapRegionA(entry.gradeId)}
                      disabled={!(selectedCellsByGrade[entry.gradeId] ?? []).length}
                      title="선택 영역을 교환영역 A로 지정 (Alt+Q)"
                    >
                      <span className="cp-btn-label-desktop">
                        {swapRegionA ? `A: 셀 ${swapRegionA.keys.length}개` : '교환영역 A'}
                      </span>
                      <span className="cp-btn-label-mobile-icon" aria-hidden>
                        A
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cp-btn-copy-paste cp-btn-swap-region"
                      onClick={() => handleSetSwapRegionB(entry.gradeId)}
                      disabled={!(selectedCellsByGrade[entry.gradeId] ?? []).length}
                      title="선택 영역을 교환영역 B로 지정 (Alt+W)"
                    >
                      <span className="cp-btn-label-desktop">
                        {swapRegionB ? `B: 셀 ${swapRegionB.keys.length}개` : '교환영역 B'}
                      </span>
                      <span className="cp-btn-label-mobile-icon" aria-hidden>
                        B
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cp-btn-copy-paste cp-btn-swap-region"
                      onClick={handleSwapRegions}
                      disabled={!swapRegionA || !swapRegionB || swapRegionA.keys.length !== swapRegionB.keys.length}
                      title="영역 A와 B 맞교환 (Alt+E)"
                    >
                      <span className="cp-btn-label-desktop">영역 바꾸기</span>
                      <span className="cp-btn-label-mobile-icon cp-btn-mobile-swap-icon" aria-hidden>
                        <span>A</span>
                        <svg viewBox="0 0 20 20" className="cp-btn-mobile-action-svg">
                          <path d="M3 6h11.2l-2.4-2.3 1.1-1.1L17.1 7l-4.2 4.4-1.1-1.1L14.2 8H3z" fill="currentColor" />
                          <path d="M17 14H5.8l2.4 2.3-1.1 1.1L2.9 13l4.2-4.4 1.1 1.1L5.8 12H17z" fill="currentColor" />
                        </svg>
                        <span>B</span>
                      </span>
                    </button>
                  </div>
                  <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드">
                    <button
                      type="button"
                      className="cp-btn-copy-paste"
                      onClick={handleCopy}
                      disabled={!(selectedCellsByGrade[entry.gradeId] ?? []).length}
                      title="선택한 셀 복사 (Ctrl+C)"
                    >
                      <span className="cp-btn-label-desktop">복사</span>
                      <span className="cp-btn-label-mobile-icon" aria-hidden>
                        <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cp-btn-copy-paste"
                      onClick={handleCut}
                      disabled={!(selectedCellsByGrade[entry.gradeId] ?? []).length}
                      title="선택한 셀 잘라내기 (Ctrl+X)"
                    >
                      <span className="cp-btn-label-desktop">잘라내기</span>
                      <span className="cp-btn-label-mobile-icon" aria-hidden>
                        <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cp-btn-copy-paste"
                      onClick={handlePaste}
                      disabled={!hasCopiedData || !(selectedCellsByGrade[entry.gradeId] ?? []).length}
                      title="붙여넣기 (Ctrl+V)"
                    >
                      <span className="cp-btn-label-desktop">붙여넣기</span>
                      <span className="cp-btn-label-mobile-icon" aria-hidden>
                        <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
                      </span>
                    </button>
                  </div>
                </div>
              );

              return (
                <article key={entry.gradeId} className="cp-card cp-base-timetable-class-card">
                  <BaseTimetableClassSectionHeader
                    classBlock={classBlock}
                  />
                  <article className="cp-card academic-calendar-settings-card cp-timetable-settings-nested-card cp-timetable-settings-base-input-card">
                    <div className="cp-grade-track-header-row">
                      <h3>기초 시간표 입력</h3>
                    </div>
                    <div className="cp-timetable-settings-base-grid-inner">
                  {/* 1학기 */}
                  <div className="cp-base-timetable-term-section">
                    <h4 className="cp-base-timetable-term-title cp-base-timetable-term-title--term1">
                      1학기 기초시간표
                    </h4>
                    <div className="cp-track-section-header cp-base-timetable-class-header">
                      {renderColorPalettes(1)}
                      {renderTermToolbar()}
                    </div>
                    {renderTermGrid(term1Grid, 1)}
                  </div>
                  {/* 1학기 → 2학기 복제 버튼 */}
                  <div className="cp-base-timetable-term-copy-row">
                    <button
                      type="button"
                      className="cp-base-timetable-term-copy-btn"
                      onClick={() => handleCopyTerm1ToTerm2(entry.gradeId)}
                    >
                      1학기 기초시간표를 2학기 기초시간표에 복제
                    </button>
                  </div>
                  {/* 2학기 */}
                  <div className="cp-base-timetable-term-section">
                    <h4 className="cp-base-timetable-term-title cp-base-timetable-term-title--term2">
                      2학기 기초시간표
                    </h4>
                    <div className="cp-track-section-header cp-base-timetable-class-header">
                      {renderColorPalettes(2)}
                    </div>
                    {renderTermGrid(term2Grid, 2)}
                  </div>
                    </div>
                </article>
                </article>
              );
          })}
        </div>
      )}
    </>
  );

  return (
    <section
      className={
        embeddedInModal ? 'cp-timetable-hub-page cp-timetable-hub-page--embedded' : 'cp-timetable-hub-page'
      }
    >
      {embeddedInModal && toolbarPortalTarget
        ? createPortal(titleActions, toolbarPortalTarget)
        : null}
      {!embeddedInModal ? (
        <div className="cp-page-title-row">
          <div>
            <h2>시간표 설정</h2>
            <p className="page-desc">
              클래스별로 시간표 색상(고정·전담·라벨링)과 기초 시간표를 설정합니다. 저장하기를 누르면 DB에 반영됩니다.
            </p>
          </div>
          {titleActions}
        </div>
      ) : null}

      {embeddedInModal ? (
        <div className="cp-timetable-hub-page-embedded-scroll">{settingsBody}</div>
      ) : (
        settingsBody
      )}

      {!embeddedInModal ? (
        <button
          type="button"
          className="cp-fab-save"
          onClick={handleSaveAll}
          disabled={loading || !isDirty}
          aria-label="저장하기"
          title="저장하기"
        >
          <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
        </button>
      ) : null}

      {programColorPickModal ? (
        <ProgramColorPickModal
          type={programColorPickModal.type}
          mode={programColorPickModal.mode}
          initialHex={programColorPickModal.initialHex}
          initialLabel={programColorPickModal.initialLabel}
          existingColors={
            programColorPickModal.type === 'project'
              ? getProjectColorsForTerm(
                  programColorsByGrade[programColorPickModal.gradeId] ?? DEFAULT_GRADE_PROGRAM_COLORS,
                  programColorPickModal.term === 2 ? 2 : 1
                )
              : (programColorsByGrade[programColorPickModal.gradeId] ?? DEFAULT_GRADE_PROGRAM_COLORS)[
                  programColorPickModal.type
                ]
          }
          onSubmit={handleSubmitProgramColor}
          onDelete={
            programColorPickModal.mode === 'edit' && programColorPickModal.initialHex
              ? handleDeleteProgramColor
              : undefined
          }
          onClose={() => setProgramColorPickModal(null)}
        />
      ) : null}

      <AutoStatusToast state={saveToastState} />
    </section>
  );
};

export default TimetableSettingsPage;
